
import React from 'react';
import { Bot } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BotForm } from './BotForm';
import { deployBot } from '@/services/bot-services';
import { DeployBotFormValues } from '@/types/bot-forms';
import { useBotDeploymentValidation } from '@/hooks/use-bot-deployment-validation';

interface DeployBotDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DeployBotDialog = ({ isOpen, onClose }: DeployBotDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canDeployBot, getDeploymentErrorMessage } = useBotDeploymentValidation();

  React.useEffect(() => {
    if (isOpen && !canDeployBot()) {
      toast({
        title: "Deployment Limit Reached",
        description: getDeploymentErrorMessage(),
        variant: "destructive"
      });
      onClose();
    }
  }, [isOpen, canDeployBot, getDeploymentErrorMessage, onClose, toast]);

  const deployBotMutation = useMutation({
    mutationFn: async (data: DeployBotFormValues) => {
      if (!user) throw new Error('User not authenticated');
      return deployBot(data, user.id);
    },
    onSuccess: () => {
      // Invalidate both userBots and resourceUsage queries to update counts
      queryClient.invalidateQueries({ 
        queryKey: ['userBots'],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({ 
        queryKey: ['resourceUsage', user?.id],
        refetchType: 'active',
      });
      
      toast({
        title: "Bot deployed successfully",
        description: "Your new bot is now active and ready to use.",
      });
      onClose();
    },
    onError: (error) => {
      console.error('Error deploying bot:', error);
      toast({
        title: "Failed to deploy bot",
        description: "There was an error deploying your bot. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: DeployBotFormValues) => {
    deployBotMutation.mutate(data);
  };

  if (!canDeployBot()) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Deploy New Bot
          </DialogTitle>
          <DialogDescription>
            Configure your new trading bot with a name, description, and target exchanges.
          </DialogDescription>
        </DialogHeader>
        
        <BotForm 
          onSubmit={handleSubmit} 
          onCancel={onClose} 
          isSubmitting={deployBotMutation.isPending}
        />
      </DialogContent>
    </Dialog>
  );
};
